import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import invoiceService, { type ConsultationInvoice, type InvoiceFilters } from '../../../services/invoiceService';
import EmptyState from '../../../components/ui/EmptyState';
import { formatCurrency } from '../../../lib/format';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';

const BillingListPage: React.FC = () => {
    const [invoices, setInvoices] = useState<ConsultationInvoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [fiscalFilter, setFiscalFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    const load = useCallback(async (filters: InvoiceFilters = {}) => {
        setLoading(true);
        setError(null);
        try {
            const result = await invoiceService.getAll({
                page,
                per_page: 20,
                search: search || undefined,
                status: statusFilter || undefined,
                fiscal_status: fiscalFilter || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                ...filters,
            });
            setInvoices(result.data ?? []);
            setTotalPages(result.last_page ?? 1);
            setTotal(result.total ?? 0);
        } catch {
            setError('Error al cargar facturas');
        } finally {
            setLoading(false);
        }
    }, [page, search, statusFilter, fiscalFilter, dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
    };

    return (
        <>
            <div className="page-header">
                <div className="row">
                    <div className="col-sm-12">
                        <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Facturación' }]} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="card-title mb-0">
                                    <i className="fa fa-file-lines me-2"></i>
                                    Facturación ({total} registros)
                                </h4>
                                <Link to="/billing/new" className="btn btn-primary btn-sm">
                                    <i className="fa fa-plus me-1"></i> Nueva Factura
                                </Link>
                            </div>

                            {/* Filters */}
                            <form onSubmit={handleSearch} className="row g-2 mb-3">
                                <div className="col-md-4">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Buscar por número, paciente..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select form-select-sm"
                                        aria-label="Filtro fiscal"
                                        value={fiscalFilter}
                                        onChange={e => { setFiscalFilter(e.target.value); setPage(1); }}
                                    >
                                        <option value="">Todos</option>
                                        <option value="fiscal">Fiscal</option>
                                        <option value="no_fiscal">No Fiscal</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select form-select-sm"
                                        value={statusFilter}
                                        onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                                    >
                                        <option value="">Todos los estados</option>
                                        <option value="draft">Borrador</option>
                                        <option value="paid">Pagado</option>
                                        <option value="cancelled">Anulado</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={dateFrom}
                                        onChange={e => setDateFrom(e.target.value)}
                                        placeholder="Desde"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={dateTo}
                                        onChange={e => setDateTo(e.target.value)}
                                        placeholder="Hasta"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <button type="submit" className="btn btn-primary btn-sm w-100">
                                        <i className="fa fa-search me-1"></i> Buscar
                                    </button>
                                </div>
                            </form>

                            {error && <div className="alert alert-danger">{error}</div>}

                            {loading ? (
                                <Spinner variant="full-page" text="Cargando facturas..." size="md" />
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table table-hover table-center mb-0">
                                            <thead>
                                                <tr>
                                                    <th>N° Recibo</th>
                                                    <th>NCF</th>
                                                    <th>Paciente</th>
                                                    <th>Doctor</th>
                                                    <th>Total</th>
                                                    <th>Estado Fiscal</th>
                                                    <th>Fecha</th>
                                                    <th>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoices.length === 0 ? (
                                                    <EmptyState variant="compact" icon="fa-file-invoice" title="No hay facturas registradas" action={{ label: 'Crear factura', to: '/billing/new', icon: 'fa-plus' }} />
                                                ) : invoices.map(inv => (
                                                    <tr key={inv.id}>
                                                        <td>
                                                            <strong className="text-primary">{inv.invoice_number}</strong>
                                                        </td>
                                                        <td>
                                                            {inv.ncf_number
                                                                ? <span className="text-muted small">{inv.ncf_number}</span>
                                                                : <span className="text-muted">—</span>}
                                                        </td>
                                                        <td>
                                                            {inv.patient ? (
                                                                <Link to={`/patients/${inv.patient_id}`}>
                                                                    {inv.patient.first_name} {inv.patient.last_name}
                                                                </Link>
                                                            ) : '—'}
                                                        </td>
                                                        <td>
                                                            {inv.doctor
                                                                ? `${inv.doctor.first_name} ${inv.doctor.last_name}`
                                                                : '—'}
                                                        </td>
                                                        <td><strong>{formatCurrency(inv.total)}</strong></td>
                                                        <td>
                                                            {inv.fiscal_status === 'cancelled' ? (
                                                                <span className="badge bg-danger">Anulada</span>
                                                            ) : inv.fiscal_status === 'issued' ? (
                                                                <span className="badge bg-success">Emitida</span>
                                                            ) : (
                                                                <span className="badge bg-secondary">Recibo</span>
                                                            )}
                                                        </td>
                                                        <td>{new Date(inv.created_at).toLocaleDateString('es-DO')}</td>
                                                        <td>
                                                            <div className="d-flex gap-1">
                                                                <Link
                                                                    to={`/billing/${inv.id}`}
                                                                    className="btn btn-sm bg-info-light"
                                                                    title="Ver detalle"
                                                                >
                                                                    <i className="fa fa-eye"></i>
                                                                </Link>
                                                                <button
                                                                    className="btn btn-sm bg-primary-light"
                                                                    title="Descargar PDF"
                                                                    onClick={() => invoiceService.downloadPdf(inv.id, inv.invoice_number)}
                                                                >
                                                                    <i className="fa fa-download"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="d-flex justify-content-center mt-3">
                                            <nav>
                                                <ul className="pagination pagination-sm mb-0">
                                                    <li className={`page-item${page === 1 ? ' disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => setPage(p => p - 1)}>
                                                            <i className="fa fa-angle-left"></i>
                                                        </button>
                                                    </li>
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                                        .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                                                        .map(p => (
                                                            <li key={p} className={`page-item${p === page ? ' active' : ''}`}>
                                                                <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                                                            </li>
                                                        ))}
                                                    <li className={`page-item${page === totalPages ? ' disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => setPage(p => p + 1)}>
                                                            <i className="fa fa-angle-right"></i>
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BillingListPage;
